/**
 * Der eine API-Client der SPA. Cookies gehen automatisch mit (same-origin
 * ueber den Vite-Proxy bzw. die API selbst). 401 → Login-Seite.
 */
import type { BenutzerZeile, Rolle, SessionUser, Sitzung, SystemStatus } from '@storeos/contracts';

export class ApiError extends Error {
  constructor(public status: number, message: string, public details?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

/** W13: CSRF-Token aus dem Cookie `storeos_csrf` (setzt die API bei /auth/sitzung und beim Login). */
function csrfToken(): string {
  const m = /(?:^|;\s*)storeos_csrf=([^;]+)/.exec(document.cookie);
  return m ? decodeURIComponent(m[1]) : '';
}

export async function api<T>(pfad: string, init: RequestInit = {}): Promise<T> {
  const schreibend = Boolean(init.method && !['GET', 'HEAD'].includes(init.method.toUpperCase()));
  const res = await fetch(`/api${pfad}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(schreibend ? { 'X-CSRF-Token': csrfToken() } : {}), ...(init.headers ?? {}) },
    credentials: 'same-origin',
  });
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 && !location.pathname.startsWith('/login')) {
      window.dispatchEvent(new CustomEvent('storeos:unauthorized'));
    }
    throw new ApiError(res.status, (body as { fehler?: string }).fehler ?? res.statusText, body);
  }
  return body as T;
}

export const post = <T,>(pfad: string, daten?: unknown) => api<T>(pfad, { method: 'POST', body: daten === undefined ? undefined : JSON.stringify(daten) });

export const systemStatus = () => api<SystemStatus>('/system/status');
/** Angemeldeter Benutzer oder null (GET /auth/sitzung antwortet immer 200). */
export const sitzung = () => api<Sitzung>('/auth/sitzung');
export const me = async () => (await sitzung()).benutzer;
/** B-S1: erster Admin mit dem Einrichtungs-Token aus dem Server-Log. */
export const einrichten = (d: { token: string; email: string; name: string; passwort: string }) => post<BenutzerZeile>('/auth/einrichtung', d);
export const eigenesPasswort = (alt: string, neu: string) => post<{ ok: boolean }>('/auth/passwort', { alt, neu });
// Benutzerverwaltung (Admin)
export const benutzerListe = () => api<BenutzerZeile[]>('/benutzer');
export const benutzerAnlegen = (d: { email: string; name: string; rolle: Rolle; passwort: string }) => post<BenutzerZeile>('/benutzer', d);
export const benutzerAendern = (id: string, d: { name?: string; rolle?: Rolle; aktiv?: boolean }) => api<BenutzerZeile>(`/benutzer/${id}`, { method: 'PATCH', body: JSON.stringify(d) });
export const benutzerPasswort = (id: string, passwort: string) => post<BenutzerZeile>(`/benutzer/${id}/passwort`, { passwort });
export const login = (email: string, passwort: string) => post<SessionUser>('/auth/login', { email, passwort });
export const logout = () => post<{ ok: boolean }>('/auth/logout');
